import { defineStore } from 'pinia';
import type { Hostname } from '@features/Pages/TimelineMapPage/_js/app/types/hostname';
import { IEpoch } from "@features/Pages/TimelineMapPage/_js/app/interfaces/IEpoch";
import { ITimelineMapItem } from "@features/Pages/TimelineMapPage/_js/app/interfaces/ITimelineMapItem";
import { IState } from "@features/Pages/TimelineMapPage/_js/app/store/IState";
import { ITranslations } from "@features/Pages/TimelineMapPage/_js/app/interfaces/ITranslations";
import {IMapboxConfig} from "@features/Pages/TimelineMapPage/_js/app/interfaces/IMapboxConfig";

export const useTimelineMapStore = defineStore('timeline', {
  state: (): IState => {
    return {
      hostname: '' as Hostname,
      showMap: false as boolean,
      showMomentsList: false as boolean,
      epochs: [] as IEpoch[],
      activeMoment: {} as ITimelineMapItem,
      showModal: false as Boolean,
      mapboxConfig: {} as IMapboxConfig,
      translations: {} as ITranslations,
      isDragging: false as Boolean,
      activeEpochId: null,
      prevActiveEpochId: null,
      isTransitioning: false as boolean,
    }
  },

  getters: {
    isJVM: (state): boolean => state.hostname === 'jvm',
    isMM: (state): boolean => state.hostname === 'mm',
    isVRAK: (state): boolean => state.hostname === 'vrak',

    activeEpoch: (state): IEpoch | null =>
        state.epochs.find((e: IEpoch) => e.id === state.activeEpochId) ?? null
  },

  actions: {    
    initEpochs(epochs: IEpoch[], initialId?: number) {
      this.epochs.splice(0, this.epochs.length, ...epochs.map(e => ({
        ...e,
        visibleCount: 0,
        startIndex: 0
      })));

      if (this.epochs.length > 0) {
        if (initialId && this.epochs.some((e: IEpoch) => e.id === initialId)) {
          this.activeEpochId = initialId;
        } else {
          this.activeEpochId = this.epochs[0].id;
        }

        const activeEpoch = this.epochs.find((e: IEpoch) => e.id === this.activeEpochId);
        if (activeEpoch) {
          activeEpoch.visibleCount = activeEpoch.timelineMapItems.length;
          activeEpoch.startIndex = 0;
        }
      }

      this.updateSurroundingEpochs();
    },

    transitionToEpoch(newId: number) {
      if (this.activeEpochId === newId || this.isTransitioning) { return; }

      this.isTransitioning = true;
      this.prevActiveEpochId = this.activeEpochId;
      this.activeEpochId = newId;

      this.updateSurroundingEpochs();

      this.isTransitioning = false;
    },

    updateSurroundingEpochs() {
      const activeIndex = this.epochs.findIndex((e: IEpoch) => e.id === this.activeEpochId);

      this.epochs.forEach((e: IEpoch) => {
        e.visibleCount = 0;
        e.startIndex = 0;
      });

      const activeEpoch = this.epochs[activeIndex];
      if (activeEpoch) {
        activeEpoch.visibleCount = activeEpoch.timelineMapItems.length;
        activeEpoch.startIndex = 0;
      }
    },

    ensureActiveEpochVisibility() {
      const active = this.epochs.find((e: IEpoch) => e.id === this.activeEpochId);
      if (!active) { return; }
      if (!active.visibleCount || active.visibleCount === 0) {
        active.visibleCount = active.timelineMapItems.length;
        active.startIndex = 0;
      }
    },

    onShowModal(moment: ITimelineMapItem) {
      if (this.isDragging) return;

      this.activeMoment = moment;
      this.showModal = true;

      const raw = (moment as any).scripts;
      let scripts: string[] = [];

      if (Array.isArray(raw)) {
        scripts = raw;
      } else if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
          try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) scripts = parsed;
          } catch {}
        } else if (trimmed) {
          scripts = [trimmed];
        }
      }

      const existing = Array.from(document.head.querySelectorAll('script')).map(s => s.src);
      scripts
        .filter((u): u is string => typeof u === 'string' && u.trim() !== '')
        .filter((u, i, arr) => arr.indexOf(u) === i)
        .forEach((url) => {
          if (!existing.includes(url)) {
            const newScript = document.createElement('script');
            newScript.src = url;
            newScript.className = 'dynamic-added-script';
            document.head.appendChild(newScript);
          }
        });
    },

    onCloseModal() {
      this.showModal = false;
      this.activeMoment = {} as ITimelineMapItem;
      document.querySelectorAll('.dynamic-added-script').forEach(script => script.remove());
    },
  },
});
